import { Component, OnInit, Input, ViewChild, ElementRef, ViewChildren, QueryList, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { ILookup } from '../common/interfaces/ILookup.interface';
import { TranslationService } from '../common/services/translation.service';
import { ControlType, TranslationHeader, TranslationSection, TranslationFormStatus } from './cpq-ml-translations.model';
import { SystemDictionaries } from '../common/models/SystemDictionaries';
import { addIfNotExists, any, except, firstOrDefault, removeIfExists } from '../common/functions/js-linq';
import { IMLTranslationService } from '../common/interfaces/IMLTranslationService.interface';
import { FormulaBuilderConfig, FormulaBuilderType } from '../cpq-formula-builder/cpq-formula-builder.model';
import { NgForm } from '@angular/forms';

@Component({
    selector: 'cpq-ml-translations',
    templateUrl: './cpq-ml-translations.component.html',
    styleUrls: ['./cpq-ml-translations.component.scss'],
    providers: [],
    standalone: false
})
export class CpqMlTranslationsComponent implements OnInit, AfterViewInit {
  @Input() service: IMLTranslationService<any>;
  @Input() header: TranslationHeader;
  @Output() onLanguageSelection: EventEmitter<number> = new EventEmitter<number>();
  @Output() onTranslationFormStatusChange = new EventEmitter();
  @ViewChild('translationsTable') translationsTable: ElementRef;
  @ViewChildren('languagesDropdown', { read: ElementRef }) private languagesDropdown: QueryList<ElementRef>;
  @ViewChild('languageTranslationForm') public languageTranslationForm: NgForm;

  public defaultDictionaryId = 0;
  public defaultDictionaryOnDomain: string;
  public selectedDictionaries: Array<ILookup<number, string>> = new Array<ILookup<number, string>>();
  public availableDictionaryItems: ILookup<number, Array<ILookup<number, string>>> = new ILookup<number, Array<ILookup<number, string>>>();
  public emptyLanguageOption: ILookup<number, string> = new ILookup<number, string>();
  public allDictionaries: Array<ILookup<number, string>> = new Array<ILookup<number, string>>();
  public canAddTranslation: boolean;
  public fbConfig: FormulaBuilderConfig;
  public translationSections: Array<TranslationSection> = [];
  public controlType = ControlType;
  constructor(
    private translationService: TranslationService
  ) { }

  ngOnInit(): void {
    this.emptyLanguageOption.id = -1;
    this.emptyLanguageOption.text = this.translationService.translate(this.header.selectLanguageKey);
    this.prepareTranslationsByDictionaryId(this.defaultDictionaryId);
    this.initDictionaries();
    this.selectedDictionaries.push(this.emptyLanguageOption);
    this.initFormulaBulider();
    this.service
      .onSave
      .subscribe(() => {
        this.save();
      });
  }

  ngAfterViewInit(): void {
    this.languageTranslationForm.statusChanges.subscribe( validationStatus => {
      this.onTranslationFormStatusChange.emit({
          valid: validationStatus === TranslationFormStatus.Valid
        });
    });
  }

  private focusAddNewTranslationButton(addedNewLanguage: boolean): void {
    if (!addedNewLanguage && this.translationsTable && this.translationsTable.nativeElement) {
      this.translationsTable.nativeElement.scrollLeft = this.translationsTable.nativeElement.scrollWidth;
    }

    if (addedNewLanguage && this.languagesDropdown.last.nativeElement) {
      setTimeout(() => (this.languagesDropdown.last.nativeElement as HTMLSelectElement).scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' }), 0);
    }
  }

  private initDictionaries(): void {
    this.translationService.getSystemDictionaries().subscribe((dictionaries: SystemDictionaries) => {
      if (dictionaries && dictionaries.systemDictionaries) {
        const defaultDictionary = firstOrDefault(dictionaries.systemDictionaries, x => x.id === dictionaries.defaultDictionaryId);
        if (defaultDictionary) {
          this.defaultDictionaryOnDomain = defaultDictionary.text;
          this.allDictionaries = dictionaries.systemDictionaries.filter(x => x.id !== defaultDictionary.id);
        }
      }
      addIfNotExists(this.allDictionaries, this.emptyLanguageOption, dictionary => dictionary.id === this.emptyLanguageOption.id, true);
      this.populateAvaliableDictionaryItems();
    });
  }

  private isEmptyOptionSelected(): boolean {
    return any(this.selectedDictionaries, dictionary => dictionary.id === this.emptyLanguageOption.id);
  }

  private save() {
    this.service
      .saveTranslations(this.translationSections)
      .subscribe(() => {
        this.service.onSaveComplete.emit();
      });
  }

  private prepareTranslationsByDictionaryId(dictionaryId: number): void {
    this.service
      .getTranslation(dictionaryId)
      .subscribe((translations: any) => {
        if (translations) {
          this.translationSections = dictionaryId > 0
            ?
            this.service.fillTranslations(translations, this.translationSections)
            :
            this.service.initTranslationDetails(translations);
        }
      });
  }

  private clearPreviouslySelectedTranslation(dictionaryId: number): void {
    this.translationSections.forEach(section => {
      if (section.translationFields) {
        section.translationFields.forEach(translationField => {
          delete translationField.translations[dictionaryId];
        });
      }
    });
  }

  private initFormulaBulider(): void {
    this.fbConfig = new FormulaBuilderConfig(FormulaBuilderType.Product);
    this.fbConfig.productId = this.service.sourceId as number;
    this.fbConfig.hideLabel = true;
  }

  private populateAvaliableDictionaryItems() {
    this.availableDictionaryItems = new ILookup<number, Array<ILookup<number, string>>>();
    this.selectedDictionaries.forEach(dictionary => {
      const filteredDictionaries = except(this.allDictionaries, this.selectedDictionaries, 'id');
      filteredDictionaries.push(dictionary);
      this.availableDictionaryItems[dictionary.id] = filteredDictionaries;
    });
  }

  public selectLanguage(selectedDictionaryId: number, previousDictionary: ILookup<number, string>) {
    const selectedDictionary = this.allDictionaries.find(x => x.id === selectedDictionaryId);
    if (selectedDictionary) {
      const indexOfPreviouseDictionary = this.selectedDictionaries.indexOf(previousDictionary);
      this.selectedDictionaries.splice(indexOfPreviouseDictionary, 1, selectedDictionary);
      if (previousDictionary.id !== this.emptyLanguageOption.id) {
        this.clearPreviouslySelectedTranslation(previousDictionary.id);
      }
      if (selectedDictionary.id !== this.emptyLanguageOption.id) {
        this.prepareTranslationsByDictionaryId(selectedDictionary.id);
        this.onLanguageSelection.emit(selectedDictionary.id);
      }
      this.canAddTranslation = !any(this.selectedDictionaries, dictionary => dictionary.id === this.emptyLanguageOption.id);
      this.focusAddNewTranslationButton(false);
      this.populateAvaliableDictionaryItems();
    }
  }

  public addNewTranslationColumn(): void {
    this.selectedDictionaries.push(this.emptyLanguageOption);
    this.canAddTranslation = false;
    this.focusAddNewTranslationButton(true);
    this.populateAvaliableDictionaryItems();
  }

  public removeTranslationColumn(dictionaryId: number): void {
    if (dictionaryId && this.selectedDictionaries.length > 1) {
      removeIfExists(this.selectedDictionaries, x => x.id === dictionaryId);
      this.clearPreviouslySelectedTranslation(dictionaryId);
      if (!this.isEmptyOptionSelected()) {
        this.canAddTranslation = true;
      }
    }
    this.populateAvaliableDictionaryItems();
  }

}
